import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { useI18n } from "@/contexts/I18nContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function SuperAdminNetworkCreate() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({
    network_key: "",
    name: "",
    display_name: "",
    main_domain: "",
    template_key: "arredamento",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/vertical-templates").then((r) => setTemplates(r.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/networks", form);
      toast.success(`Network "${data.name}" creata`);
      navigate(`/superadmin/networks/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore creazione network");
    } finally {
      setLoading(false);
    }
  };

  const selectedTpl = templates.find((t) => t.template_key === form.template_key);

  return (
    <AdminLayout>
      <div className="p-8 lg:p-10 max-w-4xl" data-testid="sa-create-network-page">
        <div className="mb-10">
          <div className="overline text-brand mb-2">SuperAdmin · Wizard</div>
          <h1 className="font-heading font-bold text-4xl tracking-tight">{t("sa_create_network")}</h1>
          <p className="text-muted-foreground mt-2">Configura una nuova rete di distribuzione in pochi step. La rete sarà immediatamente operativa.</p>
        </div>

        <form onSubmit={submit} className="bg-white border border-border rounded-sm p-8 space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="overline text-muted-foreground mb-1.5 block">{t("sa_network_key")}</label>
              <input
                data-testid="net-create-key"
                required
                value={form.network_key}
                onChange={(e) => setForm({ ...form, network_key: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })}
                className="w-full h-11 px-3 rounded-sm border border-border focus:border-brand focus:ring-1 focus:ring-brand outline-none text-sm font-mono"
                placeholder="elettronica"
              />
            </div>
            <div>
              <label className="overline text-muted-foreground mb-1.5 block">{t("sa_network_domain")}</label>
              <input
                data-testid="net-create-domain"
                required
                value={form.main_domain}
                onChange={(e) => setForm({ ...form, main_domain: e.target.value })}
                className="w-full h-11 px-3 rounded-sm border border-border focus:border-brand focus:ring-1 focus:ring-brand outline-none text-sm"
                placeholder="elettronica.totalklick.it"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="overline text-muted-foreground mb-1.5 block">{t("sa_network_name")}</label>
              <input
                data-testid="net-create-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full h-11 px-3 rounded-sm border border-border focus:border-brand focus:ring-1 focus:ring-brand outline-none text-sm"
                placeholder="Elettronica Premium"
              />
            </div>
            <div>
              <label className="overline text-muted-foreground mb-1.5 block">{t("sa_network_display")}</label>
              <input
                data-testid="net-create-display"
                required
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                className="w-full h-11 px-3 rounded-sm border border-border focus:border-brand focus:ring-1 focus:ring-brand outline-none text-sm"
                placeholder="📱 Elettronica Premium"
              />
            </div>
          </div>

          <div>
            <label className="overline text-muted-foreground mb-1.5 block">{t("sa_template")}</label>
            <Select value={form.template_key} onValueChange={(v) => setForm({ ...form, template_key: v })}>
              <SelectTrigger data-testid="net-create-template-trigger" className="h-11 rounded-sm">
                <SelectValue placeholder="Scegli template" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((tpl) => (
                  <SelectItem key={tpl.template_key} value={tpl.template_key} data-testid={`template-option-${tpl.template_key}`}>
                    {tpl.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedTpl && (
            <div className="border border-border rounded-sm p-5 bg-[#FAFAFA]">
              <div className="overline text-brand mb-3">Anteprima vertical config</div>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Terminologia</div>
                  <div className="space-y-0.5 text-xs font-mono">
                    {Object.entries(selectedTpl.config.terminology || {}).slice(0, 5).map(([k, v]) => (
                      <div key={k}><span className="text-muted-foreground">{k}:</span> {v}</div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Status</div>
                  <div className="space-y-1">
                    {(selectedTpl.config.availability_statuses || []).map((s) => (
                      <div key={s.key} className="text-xs flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                        {s.label}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-3">
            <button
              data-testid="net-create-submit"
              type="submit"
              disabled={loading}
              className="px-6 h-11 rounded-sm bg-brand hover:bg-brand-hover text-white text-sm font-medium transition-colors disabled:opacity-50"
            >
              {loading ? "..." : t("sa_create_btn")}
            </button>
            <button
              type="button"
              onClick={() => navigate("/superadmin/networks")}
              className="px-6 h-11 rounded-sm border border-border text-sm hover:bg-accent transition-colors"
            >
              Annulla
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}
